/**
 * @fileoverview Process management API for Sandbox Platform SDK
 */

import { EventEmitter } from 'eventemitter3'

/**
 * Process spawn options
 */
export interface SpawnOptions {
  /** Working directory for the process */
  cwd?: string
  /** Environment variables */
  env?: Record<string, string>
  /** Shell to use (bash, sh, zsh, etc.) */
  shell?: boolean | string
  /** Input/Output encoding */
  encoding?: 'utf8' | 'utf16le' | 'latin1' | 'base64' | 'hex'
  /** Detach the process */
  detached?: boolean
  /** Arguments for the command */
  args?: string[]
}

/**
 * Terminal size for process
 */
export interface TerminalSize {
  cols: number
  rows: number
}

/**
 * Process exit information
 */
export interface ProcessExit {
  exitCode: number | null
  signal: string | null
}

/**
 * Writable stream interface for stdin
 */
export interface WritableStream {
  write(chunk: string | Uint8Array): boolean
  end(): void
  destroy(): void
}

/**
 * Readable stream interface for stdout/stderr
 */
export interface ReadableStream extends EventEmitter {
  read(size?: number): string | Buffer | null
  setEncoding(encoding: 'utf8' | 'utf16le' | 'latin1' | 'base64' | 'hex'): void
  pause(): ReadableStream
  resume(): ReadableStream
  destroy(): void
}

/**
 * Process interface matching Node.js child_process API
 */
export interface SandboxProcess extends EventEmitter {
  /** Process ID */
  readonly pid: number
  /** Standard input stream */
  readonly stdin: WritableStream
  /** Standard output stream */
  readonly stdout: ReadableStream
  /** Standard error stream */
  readonly stderr: ReadableStream
  /** Whether the process has exited */
  readonly killed: boolean
  /** Exit code if process has exited */
  readonly exitCode: number | null
  /** Signal that terminated the process */
  readonly signalCode: string | null

  /** Kill the process */
  kill(signal?: string): boolean
  /** Resize terminal (for interactive processes) */
  resize(size: TerminalSize): void
  /** Wait for process to exit */
  wait(): Promise<ProcessExit>
}

/**
 * WebSocket-based process implementation
 */
class SandboxProcessImpl extends EventEmitter implements SandboxProcess {
  public readonly pid: number
  public readonly stdin: WritableStream
  public readonly stdout: ReadableStream
  public readonly stderr: ReadableStream
  public killed = false
  public exitCode: number | null = null
  public signalCode: string | null = null

  private httpClient: any
  private wsClient: any
  private sandboxId: string
  private processId: string
  private _killed = false

  constructor(
    httpClient: any,
    wsClient: any,
    sandboxId: string,
    processId: string,
    pid: number
  ) {
    super()
    this.httpClient = httpClient
    this.wsClient = wsClient
    this.sandboxId = sandboxId
    this.processId = processId
    this.pid = pid

    // Create stream objects
    this.stdin = new ProcessStdin(httpClient, sandboxId, processId)
    this.stdout = new ProcessStream('stdout')
    this.stderr = new ProcessStream('stderr')

    // Set up WebSocket event listeners
    this.setupWebSocketListeners()
  }

  private setupWebSocketListeners(): void {
    if (!this.wsClient) return

    this.wsClient.on('process-output', (data: any) => {
      if (data.processId !== this.processId) return

      if (data.stream === 'stdout') {
        (this.stdout as ProcessStream).push(data.data)
      } else if (data.stream === 'stderr') {
        (this.stderr as ProcessStream).push(data.data)
      }
    })

    this.wsClient.on('process-exit', (data: any) => {
      if (data.processId !== this.processId) return

      this.exitCode = data.exitCode
      this.signalCode = data.signal
      this.killed = true

      // End streams
      ;(this.stdout as ProcessStream).end()
      ;(this.stderr as ProcessStream).end()

      this.emit('exit', this.exitCode, this.signalCode)
      this.emit('close', this.exitCode, this.signalCode)
    })

    this.wsClient.on('process-error', (data: any) => {
      if (data.processId !== this.processId) return

      this.emit('error', new Error(data.error))
    })
  }

  kill(signal = 'SIGTERM'): boolean {
    if (this._killed) return false

    this._killed = true

    // Send kill request via HTTP
    this.httpClient.post(`/api/sandbox/${this.sandboxId}/process/${this.processId}/kill`, {
      signal
    }).catch((error: Error) => {
      this.emit('error', error)
    })

    return true
  }

  resize(size: TerminalSize): void {
    if (this._killed) return

    this.httpClient.post(`/api/sandbox/${this.sandboxId}/process/${this.processId}/resize`, {
      cols: size.cols,
      rows: size.rows
    }).catch((error: Error) => {
      this.emit('error', error)
    })
  }

  async wait(): Promise<ProcessExit> {
    return new Promise((resolve) => {
      if (this.killed) {
        resolve({
          exitCode: this.exitCode,
          signal: this.signalCode
        })
        return
      }

      this.once('exit', (exitCode, signal) => {
        resolve({ exitCode, signal })
      })
    })
  }
}

/**
 * Stdin implementation
 */
class ProcessStdin implements WritableStream {
  private httpClient: any
  private sandboxId: string
  private processId: string

  constructor(httpClient: any, sandboxId: string, processId: string) {
    this.httpClient = httpClient
    this.sandboxId = sandboxId
    this.processId = processId
  }

  write(chunk: string | Uint8Array): boolean {
    const data = chunk instanceof Uint8Array ?
      new TextDecoder().decode(chunk) : chunk

    this.httpClient.post(
      `/api/sandbox/${this.sandboxId}/process/${this.processId}/stdin`,
      { data }
    ).catch(() => {
      // Handle write errors silently for now
    })

    return true
  }

  end(): void {
    this.httpClient.post(
      `/api/sandbox/${this.sandboxId}/process/${this.processId}/stdin`,
      { end: true }
    ).catch(() => {
      // Handle errors silently
    })
  }

  destroy(): void {
    // No-op for now
  }
}

/**
 * Stdout/Stderr stream implementation
 */
class ProcessStream extends EventEmitter implements ReadableStream {
  private buffer: string[] = []
  private ended = false
  private encoding: 'utf8' | 'utf16le' | 'latin1' | 'base64' | 'hex' = 'utf8'

  constructor(private streamType: 'stdout' | 'stderr') {
    super()
  }

  push(data: string): void {
    if (this.ended) return

    this.buffer.push(data)
    this.emit('data', data)
  }

  end(): void {
    if (this.ended) return
    this.ended = true
    this.emit('end')
  }

  read(size?: number): string | Buffer | null {
    if (this.buffer.length === 0) return null

    const data = this.buffer.shift()
    return data || null
  }

  setEncoding(encoding: 'utf8' | 'utf16le' | 'latin1' | 'base64' | 'hex'): void {
    this.encoding = encoding
  }

  pause(): ReadableStream {
    // No-op for now
    return this
  }

  resume(): ReadableStream {
    // No-op for now
    return this
  }

  destroy(): void {
    this.buffer = []
    this.ended = true
    this.removeAllListeners()
  }
}

/**
 * Process manager for sandbox
 */
export class ProcessManager {
  private httpClient: any
  private wsClient: any
  private sandboxId: string
  private processes = new Map<string, SandboxProcess>()

  constructor(httpClient: any, wsClient: any, sandboxId: string) {
    this.httpClient = httpClient
    this.wsClient = wsClient
    this.sandboxId = sandboxId
  }

  /**
   * Spawn a new process
   */
  async spawn(
    command: string,
    args: string[] = [],
    options: SpawnOptions = {}
  ): Promise<SandboxProcess> {
    try {
      const response = await this.httpClient.post(
        `/api/sandbox/${this.sandboxId}/process/spawn`,
        {
          command,
          args,
          options: {
            cwd: options.cwd || '/',
            env: options.env || {},
            shell: options.shell || false,
            encoding: options.encoding || 'utf8',
            detached: options.detached || false
          }
        }
      )

      if (!response.success) {
        throw new Error(`Failed to spawn process: ${response.error}`)
      }

      const { processId, pid } = response.data
      const process = new SandboxProcessImpl(
        this.httpClient,
        this.wsClient,
        this.sandboxId,
        processId,
        pid
      )

      this.processes.set(processId, process)

      process.once('exit', () => {
        this.processes.delete(processId)
      })

      return process
    } catch (error) {
      throw new Error(`Failed to spawn process: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Create an interactive shell
   */
  async shell(options: SpawnOptions = {}): Promise<SandboxProcess> {
    return this.spawn('/bin/bash', ['-i'], {
      ...options,
      shell: true
    })
  }

  /**
   * Execute a command and return the result
   */
  async exec(
    command: string,
    options: SpawnOptions = {}
  ): Promise<{ stdout: string; stderr: string; exitCode: number }> {
    const process = await this.spawn('/bin/sh', ['-c', command], options)

    let stdout = ''
    let stderr = ''

    process.stdout.on('data', (data) => {
      stdout += data.toString()
    })

    process.stderr.on('data', (data) => {
      stderr += data.toString()
    })

    const { exitCode } = await process.wait()

    return {
      stdout,
      stderr,
      exitCode: exitCode || 0
    }
  }

  /**
   * Get all running processes
   */
  getProcesses(): SandboxProcess[] {
    return Array.from(this.processes.values())
  }

  /**
   * Get a process by ID
   */
  getProcess(processId: string): SandboxProcess | undefined {
    return this.processes.get(processId)
  }

  /**
   * Kill all processes
   */
  killAll(signal = 'SIGTERM'): void {
    for (const process of this.processes.values()) {
      process.kill(signal)
    }
  }

  /**
   * Clean up all processes
   */
  cleanup(): void {
    this.killAll('SIGKILL')
    this.processes.clear()
  }
}